import {
  CreateIssueRequestDto,
  CreateIssueRequestDtoSeverityEnum,
  GetIssueTemplateByIssueTypeIssueSubtypeResponseDto,
} from '@issuemanager/api-client';
import { useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type UseTemplateApplicationProps = {
  form: UseFormReturn<CreateIssueRequestDto>;
  templateData: GetIssueTemplateByIssueTypeIssueSubtypeResponseDto | undefined;
  dataUpdatedAt: number;
  issueTypeAndIssueSubtypeSelected: boolean;
};

type FieldMapping = {
  templateKey: keyof GetIssueTemplateByIssueTypeIssueSubtypeResponseDto;
  formKey: keyof CreateIssueRequestDto;
  hasValue?: () => boolean;
  transform?: (value: unknown) => unknown;
  onSet?: (value: unknown) => void;
};

export const useTemplateApplication = ({
  form,
  templateData,
  dataUpdatedAt: _dataUpdatedAt,
  issueTypeAndIssueSubtypeSelected,
}: UseTemplateApplicationProps): {
  resetTemplateState: () => void;
  lastAppliedTemplateId: string | null;
} => {
  const { t } = useTranslation(['issue']);
  const { enqueueSnackbar } = useSnackbar();

  const lastAppliedTemplateId = useRef<string | null>(null);

  const isFieldEmpty = (value: unknown): boolean => {
    if (value === undefined || value === null || value === '') {
      return true;
    }
    return Array.isArray(value) && value.length === 0;
  };

  const applyTemplate = useEventCallback(() => {
    if (
      !issueTypeAndIssueSubtypeSelected ||
      !templateData?.id ||
      templateData.id === lastAppliedTemplateId.current
    ) {
      return;
    }

    lastAppliedTemplateId.current = templateData.id;

    const fieldMappings: Array<FieldMapping> = [
      {
        templateKey: 'severity',
        formKey: 'severity',
        transform: (value) => value as CreateIssueRequestDtoSeverityEnum,
      },
      {
        templateKey: 'ownerId',
        formKey: 'ownerId',
      },
      {
        templateKey: 'dueDate',
        formKey: 'dueDate',
        hasValue: () =>
          templateData.dueDate !== null && templateData.dueDate !== undefined,
        transform: (days) => {
          const dueDate = new Date();
          dueDate.setDate(dueDate.getDate() + (days as number));
          return dueDate;
        },
      },
      {
        templateKey: 'govmapGroups',
        formKey: 'govmapGroups',
        hasValue: () => Boolean(templateData.govmapGroups?.length),
        transform: (groups) =>
          (groups as Array<{ id: string }>).map((g) => g.id),
      },
    ];

    fieldMappings.forEach(
      ({ templateKey, formKey, hasValue, transform, onSet }) => {
        const templateValue = templateData[templateKey];
        const hasTemplateValue = hasValue ? hasValue() : Boolean(templateValue);

        if (hasTemplateValue && isFieldEmpty(form.getValues(formKey))) {
          const valueToSet = transform
            ? transform(templateValue)
            : templateValue;
          form.setValue(
            formKey,
            valueToSet as CreateIssueRequestDto[typeof formKey],
          );
          onSet?.(valueToSet);
        }
      },
    );

    enqueueSnackbar(t('issue:form.template.appliedNoChanges'), {
      variant: 'info',
    });
  });

  useEffect(() => {
    if (
      issueTypeAndIssueSubtypeSelected &&
      templateData?.id &&
      templateData.id !== lastAppliedTemplateId.current
    ) {
      applyTemplate();
    }
  }, [issueTypeAndIssueSubtypeSelected, templateData?.id, applyTemplate]);

  const resetTemplateState = useEventCallback(() => {
    lastAppliedTemplateId.current = null;
  });

  return {
    resetTemplateState,
    lastAppliedTemplateId: lastAppliedTemplateId.current,
  };
};
